export const certaintyValues = ['confirmed_amount', 'included', 'excluded_amount', 'conditional', 'missing', 'unreadable'] as const;
export type Certainty = (typeof certaintyValues)[number];

export const packIds = ['moving', 'cleaning', 'repair', 'rental'] as const;
export type PackId = (typeof packIds)[number];

/** 공적 기준선이 강행규범인지 권장 기준인지. 문구 전환의 근거이며 위법 판정에는 쓰지 않는다. */
export type AnchorForce = 'mandatory' | 'advisory';

export type PackAnchor = {
  /** 기준선 문서의 공개명. UI에 그대로 노출한다. */
  doc: string;
  /** 조문·번호. 확정되지 않은 번호는 등재하지 않는다. */
  article: string;
  force: AnchorForce;
  /** 이 기준선이 적용되는 대상. 적용 밖 계약에서는 참고 기준선으로만 제시한다. */
  appliesTo: string;
  url: string;
  checkedAt: string;
};

export type ChecklistItem = {
  id: string;
  /** group 체계는 팩마다 다르다. 팩의 `groups`에 선언된 값만 허용한다. */
  group: string;
  label: string;
  aliases: string[];
  critical: boolean;
  question: string;
};

export type PackContract = {
  packId: PackId;
  /** 사용자에게 보이는 팩 이름. */
  name: string;
  /** 이 팩이 다루는 문서. */
  documentName: string;
  anchor: PackAnchor;
  /** 계약 상대에 따라 기준선 적용 여부가 갈리는 팩인지. 갈리면 첫 항목이 경로 판별이다. */
  providerBranch: boolean;
  groups: readonly string[];
  /** 서비스일 운영 캘린더 라벨. 이사는 D-7/D-1/당일, 청소는 D-1/당일/회차. */
  serviceDayLabels: readonly string[];
  checklist: readonly ChecklistItem[];
  /** false면 아키텍처 슬롯만 있고 UI에 노출하지 않는다. */
  implemented: boolean;
  /** 미구현 팩이 막혀 있는 이유와 푸는 주체. */
  blockedBy?: string;
};

export type ItemResult = {
  itemId: string;
  certainty: Certainty;
  amountKrw: number | null;
  sourceText: string | null;
  confidence: number;
  userEdited: boolean;
};

export const emptyResultsFor = (pack: PackContract): ItemResult[] =>
  pack.checklist.map((item) => ({
    itemId: item.id, certainty: 'missing', amountKrw: null, sourceText: null, confidence: 1, userEdited: false,
  }));

/** 팩 계약이 자기 선언과 어긋나지 않는지. 테스트와 런타임 조립 양쪽에서 쓴다. */
export function validatePack(pack: PackContract): string[] {
  const errors: string[] = [];
  const ids = pack.checklist.map((item) => item.id);
  if (new Set(ids).size !== ids.length) errors.push(`${pack.packId}: 항목 ID가 중복됐다`);
  for (const item of pack.checklist) {
    if (!pack.groups.includes(item.group)) errors.push(`${pack.packId}: ${item.id}의 group '${item.group}'이 팩 선언에 없다`);
    if (!item.id.startsWith(`${pack.packId === 'moving' ? 'move' : pack.packId === 'cleaning' ? 'clean' : pack.packId}-item-`)) {
      errors.push(`${pack.packId}: ${item.id}가 팩 접두사를 따르지 않는다`);
    }
    if (!item.question.trim()) errors.push(`${pack.packId}: ${item.id}에 질문 템플릿이 없다`);
  }
  if (pack.implemented && pack.checklist.length === 0) errors.push(`${pack.packId}: 구현 선언인데 체크리스트가 비었다`);
  if (!pack.implemented && !pack.blockedBy) errors.push(`${pack.packId}: 미구현 팩은 blockedBy가 필요하다`);
  return errors;
}
